import { Fab, Tooltip } from '@material-ui/core'
import SupportIcon from '@material-ui/icons/HeadsetMic'
import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { supportPageContext } from '../../helpers/supportLaunchContext'
import { useSupportScreenContext } from '../../helpers/supportScreenContext'
import { getTranslate, lang } from '../../localization'
import useStyles from './index.style'

/**
 * Where a report started, handed to the create form.
 *
 * Router state rather than a query parameter — but the reason is narrower than
 * an earlier version of this comment claimed. It said router state "can only
 * be set by this code", which is false: the value comes from
 * `useLocation().pathname`, and a pathname is part of a URL anyone can send.
 * The shell renders for any authenticated path, matched route or not, so a
 * crafted link puts crafted text on screen and one click seeds it into a
 * ticket body.
 *
 * What the choice actually buys is that the value must survive being a PATH —
 * no query string, and the shape check below rejects anything that is not
 * route-shaped. That is a real narrowing, not an elimination.
 */
export interface SupportReportOrigin {
  /** The path the reporter was on, without the language segment. */
  fromPath: string
}

/**
 * True for screens where a "report a problem here" button has nothing to say.
 *
 * The support screens themselves: a report filed from the ticket list would
 * carry the ticket list as its context, which is the one place the answer is
 * never useful. The header already has a support button for reaching them.
 */
export function isSupportRoute(pathname: string): boolean {
  return /(^|\/)support(\/|$)/.test(pathname)
}

/**
 * Strips the language segment, so a report reads the same in both locales.
 *
 * `/fa/promotions/builder` and `/en/promotions/builder` are the same screen,
 * and an agent reading a queue should not have to mentally normalise them.
 */
export function routeWithoutLang(pathname: string): string {
  return pathname.replace(/^\/(en|fa)(?=\/|$)/, '') || '/'
}

/**
 * What a path has to look like before it is carried into a ticket body.
 *
 * Route segments only: letters, digits, hyphens, underscores, dots, slashes.
 * That excludes spaces and — the case that matters — the newlines a browser
 * decodes from %0A, which is how a crafted link would otherwise compose
 * several lines of chosen prose in someone else's ticket.
 *
 * Length-capped because a path is not prose. A real route in this panel is
 * a few short segments; anything longer is not a route being reported.
 *
 * A shape check, NOT a route-table lookup. Matching against the real route
 * table would be stricter, and would also mean a report from a page that
 * 404s — a plausible thing to want to report — silently loses its context.
 * The weaker check is chosen deliberately: it removes the injection surface
 * without removing the ability to report a broken URL.
 */
const ROUTE_SHAPED = /^\/[A-Za-z0-9/._-]{0,120}$/

export function reportableOrigin(pathname: string): string | undefined {
  const path = routeWithoutLang(pathname)
  return ROUTE_SHAPED.test(path) ? path : undefined
}

/**
 * A floating "report a problem here" button.
 *
 * Distinct from the header's SupportButton, which navigates to the ticket list
 * and carries the open-ticket badge. This one starts a report FROM the screen
 * the reporter is looking at, and carries that screen with it — the single
 * fact an agent most often has to ask for and a reporter most often forgets.
 *
 * It does NOT capture a trace id. The panel never sees one: the BFF emits no
 * traceparent and has no OpenTelemetry plumbing to derive one from, so there
 * is nothing to read. The create form treats diagnostic context as optional,
 * so a ticket without it is a supported outcome rather than a degraded one.
 */
export default function SupportFab() {
  const classes = useStyles()
  const history = useHistory()
  const { pathname } = useLocation()
  const { affectedEntities, relatedPromotions, relatedDistribution } =
    useSupportScreenContext()

  if (isSupportRoute(pathname)) return null

  const fromPath = reportableOrigin(pathname)
  // Undefined rather than a fallback string: a path this cannot vouch for is
  // one the create form should not repeat back, and an unseeded form is the
  // same form the reporter gets by navigating there themselves.
  const origin = fromPath
    ? supportPageContext(pathname)
    : supportPageContext(pathname).pageKey !== 'unknown'
    ? supportPageContext(pathname)
    : undefined

  return (
    <aside aria-label={getTranslate('Support')}>
      <Tooltip title={getTranslate('Report a problem on this page')}>
        <Fab
          color="primary"
          size="medium"
          // Named for what it does, not for its icon. "Support" would collide
          // with the header button, which does something else entirely.
          aria-label={getTranslate('Report a problem on this page')}
          className={classes.fab}
          onClick={() =>
            history.push({
              pathname: `/${lang}/support/tickets/create`,
              state: origin
                ? {
                    ...origin,
                    ...(relatedDistribution?.length
                      ? { relatedDistribution }
                      : {}),
                    ...(relatedPromotions?.length ? { relatedPromotions } : {}),
                    ...(affectedEntities.length
                      ? { affectedEntities, relatedEntities: affectedEntities }
                      : {})
                  }
                : undefined
            })
          }>
          <SupportIcon />
        </Fab>
      </Tooltip>
    </aside>
  )
}
